import { useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';

/**
 * Hook for comprehensive user behavior analytics
 */
export function useAnalytics() {
  const posthog = usePostHog();
  const scrollDepthRef = useRef(0);
  const pageStartTime = useRef(Date.now());
  const interactionCount = useRef(0);
  // Cache pathname so individual event calls don't re-read it on every invocation
  const pathnameRef = useRef(window.location.pathname);
  // rAF ticking flag — prevents scroll handler from firing more than once per frame
  const scrollTickingRef = useRef(false);

  useEffect(() => {
    if (!posthog) return;

    // Keep pathname cache in sync with SPA navigation
    const updatePathname = () => { pathnameRef.current = window.location.pathname; };
    window.addEventListener('popstate', updatePathname);

    // Track page view with enhanced metadata
    const trackPageView = () => {
      posthog.capture('page_view', {
        url: pathnameRef.current,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        timestamp: new Date().toISOString()
      });
    };

    // Track scroll depth — actual work deferred to rAF so it runs at most once per frame
    const trackScrollDepth = () => {
      const scrolled = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
      const previousDepth = scrollDepthRef.current;

      if (scrolled > previousDepth) {
        if (previousDepth < 25 && scrolled >= 25) {
          posthog.capture('scroll_depth', { depth: 25, url: pathnameRef.current });
        } else if (previousDepth < 50 && scrolled >= 50) {
          posthog.capture('scroll_depth', { depth: 50, url: pathnameRef.current });
        } else if (previousDepth < 75 && scrolled >= 75) {
          posthog.capture('scroll_depth', { depth: 75, url: pathnameRef.current });
        } else if (previousDepth < 90 && scrolled >= 90) {
          posthog.capture('scroll_depth', { depth: 90, url: pathnameRef.current });
        }
        scrollDepthRef.current = scrolled;
      }
    };

    // rAF-throttled scroll listener — runs at most once per animation frame (~16ms)
    const onScroll = () => {
      if (!scrollTickingRef.current) {
        requestAnimationFrame(() => {
          trackScrollDepth();
          scrollTickingRef.current = false;
        });
        scrollTickingRef.current = true;
      }
    };

    // Track time on page
    const trackTimeOnPage = () => {
      const timeSpent = Date.now() - pageStartTime.current;
      if (timeSpent > 10000) {
        posthog.capture('time_on_page', {
          duration_ms: timeSpent,
          duration_seconds: Math.round(timeSpent / 1000),
          interactions: interactionCount.current,
          scroll_depth: scrollDepthRef.current,
          url: pathnameRef.current
        });
      }
    };

    // Track user interactions
    const trackInteraction = (event) => {
      interactionCount.current += 1;
      if (event.target.closest('[data-track]')) {
        const trackingData = event.target.closest('[data-track]').dataset.track;
        posthog.capture('ui_interaction', {
          element: trackingData,
          interaction_type: event.type,
          url: pathnameRef.current,
          timestamp: new Date().toISOString()
        });
      }
    };

    // Track performance issues
    const trackPerformanceIssue = (entry) => {
      if (entry.duration > 100) {
        posthog.capture('performance_issue', {
          type: 'long_task',
          duration: Math.round(entry.duration),
          start_time: Math.round(entry.startTime),
          url: pathnameRef.current
        });
      }
    };

    trackPageView();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('beforeunload', trackTimeOnPage);
    document.addEventListener('click', trackInteraction);
    document.addEventListener('keydown', trackInteraction);

    // Only observe longtask if the browser supports it
    let observer = null;
    if (
      'PerformanceObserver' in window &&
      PerformanceObserver.supportedEntryTypes &&
      PerformanceObserver.supportedEntryTypes.includes('longtask')
    ) {
      try {
        observer = new PerformanceObserver((list) => {
          list.getEntries().forEach(trackPerformanceIssue);
        });
        observer.observe({ entryTypes: ['longtask'] });
      } catch (e) {
        // Silently ignore — longtask observation is non-critical
      }
    }

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('popstate', updatePathname);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('beforeunload', trackTimeOnPage);
      document.removeEventListener('click', trackInteraction);
      document.removeEventListener('keydown', trackInteraction);
    };
  }, [posthog]);

  return {
    trackEvent: (eventName, properties = {}) => {
      if (posthog) {
        posthog.capture(eventName, {
          ...properties,
          url: pathnameRef.current,
          timestamp: new Date().toISOString()
        });
      }
    },

    trackError: (error, context = {}) => {
      if (posthog) {
        posthog.capture('error_occurred', {
          error_message: error.message,
          error_stack: error.stack,
          error_name: error.name,
          context,
          url: pathnameRef.current,
          timestamp: new Date().toISOString()
        });
      }
      if (process.env.NODE_ENV === 'development') {
        console.error('📊 Tracked Error:', error, context);
      }
    },

    trackFeatureUsage: (feature, action, metadata = {}) => {
      if (posthog) {
        posthog.capture('feature_usage', {
          feature,
          action,
          ...metadata,
          url: pathnameRef.current,
          timestamp: new Date().toISOString()
        });
      }
    }
  };
}
